import { Logger } from '@frontend/logger';
import { ErrorBoundary } from '@sentry/react';
import React from 'react';

import { KioskErrorFallback } from './kiosk-error-fallback';
import { MobileErrorFallback } from './mobile-error-fallback';
import { PlatformErrorFallback } from './platform-error-fallback';
import './sentry-error-boundary.css';

function detectVariant(): 'kiosk' | 'mobile' | 'platform' {
    const host = window.location.host;

    // Kiosk / terminal devices
    if (host.includes('terminal') || host.includes('kiosk') || host.includes('localhost:8') || host.includes('localhost:3000')) {
        return 'kiosk';
    }

    // Mobile app host, or any narrow-screen touch device
    if (host.includes('mobile') || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768) {
        return 'mobile';
    }

    return 'platform';
}

function DefaultFallback({ error, eventId, resetError }: { error: Error; eventId: string; resetError: () => void }) {
    Logger.error('Error caught by SentryErrorBoundary:', error, 'Event ID:', eventId);

    const variant = detectVariant();

    if (variant === 'kiosk') {
        return (
            <KioskErrorFallback
                error={error}
                eventId={eventId}
                resetError={resetError}
            />
        );
    }

    if (variant === 'mobile') {
        return (
            <MobileErrorFallback
                error={error}
                eventId={eventId}
                resetError={resetError}
            />
        );
    }

    return (
        <PlatformErrorFallback
            error={error}
            eventId={eventId}
            resetError={resetError}
        />
    );
}

interface SentryErrorBoundaryProps {
    children: React.ReactNode;
}

export function SentryErrorBoundary({ children }: SentryErrorBoundaryProps) {
    return (
        <ErrorBoundary
            showDialog={false}
            fallback={({ error, eventId, resetError }) => (
                <DefaultFallback
                    error={error as Error}
                    eventId={eventId}
                    resetError={resetError}
                />
            )}>
            {children}
        </ErrorBoundary>
    );
}
